import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Users, MapPin, Wallet, Compass, Star } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import useTitle from "../lib/useTitle";
import { TYPE_META, CONTACT } from "../lib/constants";
import AccommodationCard from "../components/AccommodationCard";

const heroImg = "https://images.unsplash.com/photo-1714561690635-6430252d9502?w=2000&q=80";
const vibeImg = "https://images.unsplash.com/photo-1727425836905-0c2566febca3?w=1400";
const interiorImg = "https://images.unsplash.com/photo-1709805619372-40de3f158e83?w=1400";
const desertImg = "https://images.unsplash.com/photo-1661622960824-86b510e68449?w=1400";

const whyIcons = [Users, Compass, Wallet, MapPin];
const whyAccents = ["#1FB6D9", "#7FA88B", "#FFD24A", "#1E3A8A"];

const HostelworldMark = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
    <circle cx="16" cy="16" r="16" fill="#FF7C2A" />
    <path d="M9 11h2.5v3.6h3V11H17v10h-2.5v-4H11.5v4H9V11zm10.4 0h2.6l1.2 6.4 1.2-6.4H27l-2.4 10h-2.5l-1.2-5.7-1.2 5.7H17.2l-2.4-10h2.6l1.2 6.4 1.2-6.4z" fill="white"/>
  </svg>
);

export default function Home() {
  const { t } = useI18n();
  useTitle();
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/accommodations", { params: { featured: true } })
      .then((r) => setFeatured(r.data.slice(0, 3)))
      .catch(() => setFeatured([]));
  }, []);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-end overflow-hidden bg-wostel-navy">
        <img src={heroImg} alt="Djerba" className="absolute inset-0 w-full h-full object-cover opacity-90" />
        <div className="absolute inset-0 bg-gradient-to-t from-wostel-navy/85 via-wostel-navy/30 to-transparent" />
        <div className="container-x relative z-10 pb-20 pt-32 text-sand-50">
          <motion.span initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="eyebrow text-wostel-sun mb-6 inline-block">{t.hero.eyebrow}</motion.span>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}
            className="font-extrabold text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight max-w-5xl">
            {t.hero.title} <span className="text-wostel-sun">{t.hero.titleAccent}</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.25 }}
            className="mt-8 max-w-2xl text-lg text-sand-50/85 leading-relaxed">{t.hero.sub}</motion.p>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-10 flex flex-wrap gap-4">
            <Link to="/stays" data-testid="hero-stays-cta" className="btn-pill bg-wostel-sun text-wostel-ink hover:brightness-95">
              {t.hero.ctaPrimary} <ArrowRight size={16} />
            </Link>
            <Link to="/guides" data-testid="hero-guides-cta" className="btn-pill bg-transparent text-sand-50 border border-sand-50/40 hover:bg-sand-50/10">
              {t.hero.ctaSecondary}
            </Link>
          </motion.div>
        </div>
      </section>

      {/* RATINGS BAND */}
      <section className="bg-sand-50 border-b border-sand-300/60" data-testid="ratings-section">
        <div className="container-x py-10 grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
          <span className="eyebrow text-wostel-ink/70 md:text-left text-center">{t.ratings.eyebrow}</span>
          <div className="flex items-center justify-center gap-4 border-l border-r border-sand-300/60 px-4 py-2" data-testid="rating-google">
            <div className="flex items-center gap-1.5">
              <svg width="28" height="28" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3a12 12 0 1 1-3.4-13l5.7-5.7A20 20 0 1 0 44 24c0-1.2-.1-2.4-.4-3.5z"/>
                <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8A12 12 0 0 1 24 12c3 0 5.7 1.1 7.8 3l5.7-5.7A20 20 0 0 0 6.3 14.7z"/>
                <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.5-5.2l-6.2-5.3A12 12 0 0 1 12.7 28l-6.5 5A20 20 0 0 0 24 44z"/>
                <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.6 1.7-1.6 3.2-3 4.3l6.2 5.2c-.4.4 6.5-4.7 6.5-13.5 0-1.2-.1-2.4-.4-3.5z"/>
              </svg>
              <span className="text-3xl font-extrabold tracking-tight">4.9</span>
            </div>
            <div className="text-sm text-wostel-ink/70 leading-tight">
              <div className="flex items-center gap-0.5 text-wostel-sun">
                {[...Array(5)].map((_, i) => <Star key={i} size={12} fill="currentColor" stroke="none"/>)}
              </div>
              <div className="mt-0.5">{t.ratings.google}</div>
            </div>
          </div>
          <div className="flex items-center justify-center gap-4" data-testid="rating-hostelworld">
            <HostelworldMark />
            <div className="leading-tight">
              <div className="text-3xl font-extrabold tracking-tight">9.8<span className="text-base font-medium text-wostel-ink/50">/10</span></div>
              <div className="text-sm text-wostel-ink/70">{t.ratings.hostelworld}</div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WE OFFER — bento */}
      <section className="container-x py-28" data-testid="what-we-offer">
        <div className="max-w-3xl mb-16">
          <span className="eyebrow">{t.offer.eyebrow}</span>
          <h2 className="font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.98] mt-4">
            {t.offer.title} <span className="text-wostel-cyan">{t.offer.titleAccent}</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          <Link to="/stays?type=hostel" data-testid="offer-hostel" className="md:col-span-7 md:row-span-2 group relative rounded-3xl overflow-hidden p-10 min-h-[420px] flex flex-col justify-end transition-all hover:-translate-y-1" style={{ background: TYPE_META.hostel.bgSoft }}>
            <img src={vibeImg} alt="" className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-90 transition" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
            <div className="relative text-sand-50">
              <img src={TYPE_META.hostel.logo} alt="" className="h-16 w-16 mb-6" />
              <h3 className="font-extrabold text-3xl sm:text-4xl mb-3 tracking-tight">{t.offer.hostel.name}</h3>
              <p className="max-w-md text-sand-50/85 leading-relaxed">{t.offer.hostel.desc}</p>
              <div className="mt-6 inline-flex items-center gap-2 text-sm font-medium">{t.nav.book} <ArrowRight size={14}/></div>
            </div>
          </Link>

          <Link to="/stays?type=guest_house" data-testid="offer-guesthouse" className="md:col-span-5 group relative rounded-3xl overflow-hidden p-8 min-h-[200px] flex flex-col justify-between transition-all hover:-translate-y-1" style={{ background: TYPE_META.guest_house.bgSoft }}>
            <div className="flex items-start justify-between">
              <img src={TYPE_META.guest_house.logo} alt="" className="h-14 w-14" />
              <ArrowRight size={20} className="opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition" />
            </div>
            <div>
              <h3 className="font-extrabold text-2xl sm:text-3xl mb-2 tracking-tight">{t.offer.guest_house.name}</h3>
              <p className="text-sm text-wostel-ink/70 leading-relaxed">{t.offer.guest_house.desc}</p>
            </div>
          </Link>

          <Link to="/stays?type=apartment" data-testid="offer-apartment" className="md:col-span-5 group relative rounded-3xl overflow-hidden p-8 min-h-[200px] flex flex-col justify-between transition-all hover:-translate-y-1 text-sand-50" style={{ background: TYPE_META.apartment.color }}>
            <div className="flex items-start justify-between">
              <img src={TYPE_META.apartment.logo} alt="" className="h-14 w-14" />
              <ArrowRight size={20} className="opacity-70 group-hover:opacity-100 group-hover:translate-x-1 transition" />
            </div>
            <div>
              <h3 className="font-extrabold text-2xl sm:text-3xl mb-2 tracking-tight">{t.offer.apartment.name}</h3>
              <p className="text-sm text-sand-50/80 leading-relaxed">{t.offer.apartment.desc}</p>
            </div>
          </Link>
        </div>
      </section>

      {/* WHY CHOOSE WOSTEL */}
      <section className="bg-sand-100 py-28" data-testid="why-section">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="eyebrow">{t.why.eyebrow}</span>
            <h2 className="font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1] mt-4">
              {t.why.title} <span className="text-wostel-cyan">{t.why.titleAccent}</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.why.cards.map((c, i) => {
              const Icon = whyIcons[i];
              return (
                <motion.article
                  key={i}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="bg-white rounded-3xl p-8 border border-sand-300/60 hover:shadow-xl transition-all"
                  data-testid={`why-card-${i}`}
                >
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6" style={{ background: whyAccents[i], color: i === 2 ? "#1A1A1A" : "white" }}>
                    <Icon size={22} strokeWidth={2} />
                  </div>
                  <h3 className="font-extrabold text-xl mb-3 tracking-tight">{c.title}</h3>
                  <p className="text-sm text-wostel-ink/70 leading-relaxed">{c.desc}</p>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="container-x py-28" data-testid="featured-section">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <span className="eyebrow">{t.featured.eyebrow}</span>
              <h2 className="font-extrabold text-4xl sm:text-5xl tracking-tight mt-3">{t.featured.title}</h2>
            </div>
            <Link to="/stays" className="text-sm font-medium hover:text-wostel-cyan inline-flex items-center gap-2" data-testid="see-all-stays">
              {t.nav.stays} <ArrowRight size={14}/>
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featured.map((acc) => <AccommodationCard key={acc.id} acc={acc} />)}
          </div>
        </section>
      )}

      {/* SEFER TUNISIA / ACTIVITIES */}
      <section className="bg-sand-200 py-24" data-testid="activities-section">
        <div className="container-x grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6 relative">
            <div className="arch-shape overflow-hidden aspect-[4/5] max-w-md">
              <img src={desertImg} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -right-6 hidden md:block">
              <div className="bg-wostel-sun text-wostel-ink px-6 py-4 rounded-2xl rotate-3 shadow-lg flex items-center gap-3">
                <Sparkles size={18}/>
                <div className="font-extrabold tracking-tight">Matmata · Tataouine</div>
              </div>
            </div>
          </div>
          <div className="md:col-span-6">
            <span className="eyebrow">{t.activities.eyebrow}</span>
            <h2 className="font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4 leading-[0.98]">
              {t.activities.title} <span className="text-wostel-sage">{t.activities.titleAccent}</span>
            </h2>
            <p className="mt-6 text-lg text-wostel-ink/75 leading-relaxed max-w-lg">{t.activities.desc}</p>
            <a href={CONTACT.sefertunisia} target="_blank" rel="noreferrer" data-testid="activities-cta"
               className="btn-pill bg-wostel-ink text-sand-50 mt-8 hover:bg-wostel-sage">
              {t.activities.cta} <ArrowRight size={16}/>
            </a>
          </div>
        </div>
      </section>

      {/* SECONDARY CTA */}
      <section className="container-x py-24" data-testid="secondary-cta">
        <div className="grid md:grid-cols-2 gap-6">
          <Link to="/guides" className="group rounded-3xl overflow-hidden relative aspect-[16/9] md:aspect-auto md:min-h-[280px]" data-testid="cta-guides">
            <img src={interiorImg} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-tr from-wostel-navy/80 to-transparent" />
            <div className="relative h-full flex flex-col justify-end p-10 text-sand-50">
              <span className="eyebrow text-wostel-sun">{t.nav.guides}</span>
              <h3 className="font-extrabold text-3xl sm:text-4xl mt-2 tracking-tight">{t.guides.title}</h3>
            </div>
          </Link>
          <Link to="/creators" className="group rounded-3xl overflow-hidden relative aspect-[16/9] md:aspect-auto md:min-h-[280px]" data-testid="cta-creators">
            <div className="absolute inset-0 bg-wostel-sun" />
            <div className="absolute inset-0 grain opacity-50" />
            <div className="relative h-full flex flex-col justify-end p-10 text-wostel-ink">
              <span className="eyebrow">{t.nav.creator}</span>
              <h3 className="font-extrabold text-3xl sm:text-4xl mt-2 max-w-md tracking-tight">{t.creator.title}</h3>
            </div>
          </Link>
        </div>
      </section>
    </main>
  );
}
